/**
 * @file MigrationRows.tsx
 * @description Строки «Проставить вид по категориям» (phase19.22, W22, макет
 * §4): категория · товаров без вида · выбор вида. Карточки до md, таблица
 * с md.
 *
 * Вариант, совпавший с предложением сервера, помечен «✓ предложено»; строка
 * без выбора — янтарным «выберите вид»: её пропустят, товары останутся «не
 * задан». Выбор — `SelectField` (нативный select запрещён).
 */

import { Check, TriangleAlert } from "lucide-react";

import { SelectField, type SelectFieldOption } from "../../ui/SelectField";
import type { AssignPreviewCategory } from "../../../lib/api-product-kinds";

interface Props {
  rows: AssignPreviewCategory[];
  /** categoryId → productKindId; "" — не выбрано. */
  value: Record<string, string>;
  onChange: (categoryId: string, kindId: string) => void;
  kindOptions: SelectFieldOption[];
  disabled?: boolean;
}

export function MigrationRows({ rows, value, onChange, kindOptions, disabled }: Props) {
  const options: SelectFieldOption[] = [{ value: "", label: "— выберите вид —" }, ...kindOptions];

  return (
    <>
      <ul className="flex flex-col gap-3 md:hidden">
        {rows.map((row) => (
          <li key={row.id} className="flex flex-col gap-1.5 rounded-xl bg-surface-low p-3">
            <div className="flex items-baseline justify-between gap-2">
              <span className="min-w-0 text-sm font-medium text-on-surface">{row.displayName}</span>
              <span className="shrink-0 text-xs text-on-surface-variant tabular-nums">
                {row.productsCount} тов.
              </span>
            </div>
            {row.path !== row.displayName ? (
              <span className="text-xs text-on-surface-variant">{row.path}</span>
            ) : null}
            <SelectField
              ariaLabel={`Вид для категории ${row.displayName}`}
              value={value[row.id] ?? ""}
              onChange={(v) => onChange(row.id, v)}
              options={options}
              disabled={disabled}
            />
            <RowHint row={row} selected={value[row.id] ?? ""} />
          </li>
        ))}
      </ul>

      <table className="hidden w-full text-sm md:table">
        <thead>
          <tr className="text-left text-xs text-on-surface-variant">
            <th className="py-2 pr-3 font-medium">Категория</th>
            <th className="py-2 pr-3 text-right font-medium">Товаров</th>
            <th className="py-2 font-medium">Вид</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id} className="border-t border-outline-variant align-top">
              <td className="py-2 pr-3">
                <div className="font-medium text-on-surface">{row.displayName}</div>
                {row.path !== row.displayName ? (
                  <div className="text-xs text-on-surface-variant">{row.path}</div>
                ) : null}
              </td>
              <td className="py-2 pr-3 text-right tabular-nums">{row.productsCount}</td>
              <td className="py-2">
                <div className="flex flex-col gap-1">
                  <SelectField
                    ariaLabel={`Вид для категории ${row.displayName}`}
                    value={value[row.id] ?? ""}
                    onChange={(v) => onChange(row.id, v)}
                    options={options}
                    disabled={disabled}
                  />
                  <RowHint row={row} selected={value[row.id] ?? ""} />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

/** Подсказка под выбором: предложение сервера или «строку пропустят». */
function RowHint({ row, selected }: { row: AssignPreviewCategory; selected: string }) {
  if (!selected) {
    return (
      <span className="flex items-center gap-1 text-xs text-warning">
        <TriangleAlert className="size-3.5" aria-hidden />
        Не выбран — товары останутся без вида
      </span>
    );
  }
  if (row.suggestedKindId && row.suggestedKindId === selected) {
    return (
      <span className="flex items-center gap-1 text-xs text-primary">
        <Check className="size-3.5" aria-hidden />
        Предложено по названию категории
      </span>
    );
  }
  return null;
}
